import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Menu, ChevronDown, LogIn, LayoutDashboard } from "lucide-react";
import { Sheet, SheetContent, SheetTrigger, SheetClose } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import Logo from "@/components/Logo";
import LanguageSelector from "@/components/LanguageSelector";
import { getServices, getWallets } from "@/data/site";
import { useAuth, roleHome } from "@/context/AuthContext";
import { useI18n } from "@/context/I18nContext";

const RESOURCES_PLACEHOLDER = null;

function NavDropdown({ label, items, testid }) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          data-testid={testid}
          className="inline-flex items-center gap-1 text-sm text-slate-300 hover:text-white transition-colors"
        >
          {label}
          <ChevronDown className="h-3.5 w-3.5" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="min-w-[240px]">
        {items.map((it) => (
          <DropdownMenuItem key={it.to} asChild>
            <Link to={it.to} data-testid={`nav-item-${it.to}`}>{it.label}</Link>
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export default function PublicHeader() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { user } = useAuth();
  const { t, lang } = useI18n();
  const navigate = useNavigate();

  const RESOURCES = [
    { to: "/blog", label: t("nav.blog") },
    { to: "/knowledge-base", label: t("nav.kb") },
    { to: "/faq", label: t("nav.faq") },
    { to: "/criptovalute", label: t("nav.crypto") },
    { to: "/sicurezza", label: t("nav.security") },
  ];

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const serviceItems = getServices(lang).map((s) => ({ to: `/servizi/${s.slug}`, label: s.title }));
  const walletItems = getWallets(lang).map((w) => ({ to: `/wallet/${w.slug}`, label: w.name }));

  return (
    <header
      data-testid="public-header"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${
        scrolled ? "rc-glass border-b border-white/10" : "bg-transparent"
      }`}
    >
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="flex h-16 items-center justify-between gap-4">
          <Logo />

          <nav className="hidden lg:flex items-center gap-6 whitespace-nowrap">
            <Link to="/" className="text-sm text-slate-300 hover:text-white transition-colors" data-testid="nav-home">{t("nav.home")}</Link>
            <NavDropdown label={t("nav.services")} items={[{ to: "/servizi", label: t("nav.services_all") }, ...serviceItems]} testid="nav-services" />
            <NavDropdown label={t("nav.wallets")} items={[{ to: "/wallet", label: t("nav.wallets_all") }, ...walletItems]} testid="nav-wallets" />
            <Link to="/come-funziona" className="text-sm text-slate-300 hover:text-white transition-colors" data-testid="nav-how">{t("nav.how")}</Link>
            <Link to="/prezzi" className="text-sm text-slate-300 hover:text-white transition-colors" data-testid="nav-pricing">{t("nav.pricing")}</Link>
            <NavDropdown label={t("nav.resources")} items={RESOURCES} testid="nav-resources" />
            <Link to="/chi-siamo" className="text-sm text-slate-300 hover:text-white transition-colors" data-testid="nav-about">{t("nav.about")}</Link>
          </nav>

          <div className="hidden lg:flex items-center gap-2.5">
            <LanguageSelector />
            {user ? (
              <Button asChild variant="outline" className="border-white/20 text-white hover:bg-white/5" data-testid="nav-dashboard">
                <Link to={roleHome(user.role)}><LayoutDashboard className="h-4 w-4 mr-1.5" />{t("cta.dashboard")}</Link>
              </Button>
            ) : (
              <Button asChild variant="ghost" className="text-slate-200 hover:text-white hover:bg-white/5" data-testid="nav-login">
                <Link to="/login"><LogIn className="h-4 w-4 mr-1.5" />{t("cta.login")}</Link>
              </Button>
            )}
            <Button asChild className="bg-gold text-navy hover:bg-gold-hover font-medium" data-testid="nav-start-recovery">
              <Link to="/avvia-recupero">{t("cta.start")}</Link>
            </Button>
          </div>

          {/* Mobile */}
          <div className="flex lg:hidden items-center gap-2">
            <LanguageSelector />
            <Sheet open={open} onOpenChange={setOpen}>
              <SheetTrigger asChild>
                <button data-testid="mobile-menu-trigger" className="inline-flex h-10 w-10 items-center justify-center rounded-md border border-white/10 text-white">
                  <Menu className="h-5 w-5" />
                </button>
              </SheetTrigger>
              <SheetContent side="right" className="w-[85vw] bg-navy-paper border-white/10 overflow-y-auto">
                <div className="mt-6 flex flex-col gap-1">
                  {[
                    { to: "/", label: t("nav.home") },
                    { to: "/servizi", label: t("nav.services") },
                    { to: "/wallet", label: t("nav.wallets") },
                    { to: "/come-funziona", label: t("nav.how") },
                    { to: "/prezzi", label: t("nav.pricing") },
                    { to: "/blog", label: t("nav.blog") },
                    { to: "/knowledge-base", label: t("nav.kb") },
                    { to: "/faq", label: t("nav.faq") },
                    { to: "/chi-siamo", label: t("nav.about") },
                    { to: "/contatti", label: t("nav.contact") },
                  ].map((it) => (
                    <SheetClose asChild key={it.to}>
                      <Link to={it.to} data-testid={`mobile-nav-${it.to}`} className="rounded-md px-3 py-2.5 text-slate-200 hover:bg-white/5">
                        {it.label}
                      </Link>
                    </SheetClose>
                  ))}
                  <div className="mt-4 flex flex-col gap-2">
                    <SheetClose asChild>
                      <Button onClick={() => navigate(user ? roleHome(user.role) : "/login")} variant="outline" className="border-white/20 text-white w-full">
                        {user ? t("cta.dashboard") : t("cta.login")}
                      </Button>
                    </SheetClose>
                    <SheetClose asChild>
                      <Button asChild className="bg-gold text-navy hover:bg-gold-hover w-full">
                        <Link to="/avvia-recupero">{t("cta.start")}</Link>
                      </Button>
                    </SheetClose>
                  </div>
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </div>
    </header>
  );
}
